import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { useImpresion } from '../../contexts/ContextoImpresion';
import qzSerialService from '../../services/qzSerialService';
import qzHidService from '../../services/qzHidService';
import { parsearPeso } from '../../services/protocolosBalanza';
import SpinnerCargando from '../util/SpinnerCargando';
import { FaCheckCircle, FaTimesCircle, FaSync } from 'react-icons/fa';
import { FiCpu, FiActivity, FiRefreshCw } from 'react-icons/fi';

const MODOS_SCANNER = [
  { value: 'hid', label: 'HID Keyboard Wedge (recomendado)' },
  { value: 'qz_hid', label: 'QZ Tray HID (control avanzado)' },
  { value: 'deshabilitado', label: 'Deshabilitado' },
];

const KNOWN_SCANNER_VENDORS = {
  '0x05E0': 'Zebra/Symbol',
  '0x05e0': 'Zebra/Symbol',
  '0x0C2E': 'Honeywell',
  '0x0c2e': 'Honeywell',
  '0x05F9': 'Datalogic',
  '0x05f9': 'Datalogic',
  '0x04B4': 'Cypress (HID genérico)',
  '0x04b4': 'Cypress (HID genérico)',
  '0x0C27': 'Opticon',
  '0x0c27': 'Opticon',
};

function etiquetaDispositivo(dev) {
  const vendorLabel = KNOWN_SCANNER_VENDORS[dev.vendorId];
  const desc = dev.product || dev.manufacturer || '';
  if (vendorLabel) return `${vendorLabel}${desc ? ' — ' + desc : ''}`;
  return desc || 'Dispositivo HID';
}

function esScanner(dev) {
  return !!KNOWN_SCANNER_VENDORS[dev.vendorId];
}

const CONEXIONES_BALANZA = [
  { value: 'deshabilitado', label: 'Deshabilitada' },
  { value: 'serial', label: 'Puerto Serial (RS-232 / USB-Serial)' },
];

const PROTOCOLOS = [
  { value: 'zebra', label: 'Zebra' },
  { value: 'toledo', label: 'Mettler Toledo' },
  { value: 'cas', label: 'CAS' },
  { value: 'generico', label: 'Genérico' },
];

const BAUDRATES = [
  { value: 4800, label: '4800' },
  { value: 9600, label: '9600 (estándar)' },
  { value: 19200, label: '19200' },
  { value: 38400, label: '38400' },
];

function ConfiguracionPOS() {
  const [cajas, setCajas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editandoId, setEditandoId] = useState(null);
  const [formEdit, setFormEdit] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [puertosSerial, setPuertosSerial] = useState([]);
  const [refrescandoPuertos, setRefrescandoPuertos] = useState(false);
  const [probandoId, setProbandoId] = useState(null);
  const [pesoTest, setPesoTest] = useState(null);
  const [dispositivosHid, setDispositivosHid] = useState([]);
  const [buscandoHid, setBuscandoHid] = useState(false);
  const [autoDetectando, setAutoDetectando] = useState(false);
  const [autoDetectProgreso, setAutoDetectProgreso] = useState('');

  const { qzConectado } = useImpresion();

  const cargarCajas = useCallback(async () => {
    try {
      setCargando(true);
      const resp = await instanciaAxios.get('/cuenta_tesoreria/cajas-config-pos');
      setCajas(resp.data?.data || []);
    } catch (err) {
      toast.error('Error al cargar configuración POS');
      console.error(err);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarCajas(); }, [cargarCajas]);

  const refrescarPuertos = async () => {
    setRefrescandoPuertos(true);
    try {
      const ports = await qzSerialService.listarPuertos();
      setPuertosSerial(ports);
      if (ports.length === 0) toast.info('No se encontraron puertos seriales');
    } catch {
      toast.error('Error al listar puertos');
    } finally {
      setRefrescandoPuertos(false);
    }
  };

  const iniciarEdicion = (caja) => {
    setEditandoId(caja.id_cuenta_tesoreria);
    setFormEdit({
      modo_scanner: caja.modo_scanner || 'hid',
      conexion_balanza: caja.conexion_balanza || 'deshabilitado',
      puerto_balanza: caja.puerto_balanza || '',
      protocolo_balanza: caja.protocolo_balanza || 'zebra',
      baudrate_balanza: caja.baudrate_balanza || 9600,
      scanner_vendor_id: caja.scanner_vendor_id || '',
      scanner_product_id: caja.scanner_product_id || '',
    });
    setPesoTest(null);
    if (qzConectado) refrescarPuertos();
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setFormEdit({});
    setPesoTest(null);
  };

  const guardarEdicion = async (idCaja) => {
    try {
      setGuardando(true);
      await instanciaAxios.put(`/cuenta_tesoreria/${idCaja}/config-pos`, formEdit);
      toast.success('Configuración POS guardada');
      setEditandoId(null);
      setFormEdit({});
      await cargarCajas();
    } catch (err) {
      toast.error('Error al guardar la configuración');
      console.error(err);
    } finally {
      setGuardando(false);
    }
  };

  const probarBalanza = async (idCaja) => {
    if (!formEdit.puerto_balanza) {
      toast.warning('Seleccione un puerto serial primero');
      return;
    }
    setProbandoId(idCaja);
    setPesoTest(null);
    const protocolo = formEdit.protocolo_balanza || 'zebra';
    try {
      let datoRecibido = null;
      qzSerialService.setCallbackSerial((evt) => {
        if (evt?.type === 'ERROR') {
          console.error('[Balanza Test] Error serial:', evt.exception);
          return;
        }
        if (evt?.output) {
          const resultado = parsearPeso(protocolo, evt.output);
          if (resultado) datoRecibido = resultado;
        }
      });

      const ok = await qzSerialService.conectarPuerto(formEdit.puerto_balanza, {
        baudRate: Number(formEdit.baudrate_balanza) || 9600,
        protocolo,
      });

      if (!ok) {
        toast.error('No se pudo abrir el puerto');
        return;
      }

      await qzSerialService.enviarComandoProtocolo(formEdit.puerto_balanza, protocolo);

      await new Promise(r => setTimeout(r, 3000));
      await qzSerialService.desconectarPuerto(formEdit.puerto_balanza);

      if (datoRecibido) {
        setPesoTest(datoRecibido);
        toast.success(`Peso leído: ${datoRecibido.peso} ${datoRecibido.unidad}`);
      } else {
        toast.warning('No se recibieron datos de la balanza en 3 segundos');
      }
    } catch (err) {
      toast.error('Error al probar la balanza');
      console.error(err);
    } finally {
      setProbandoId(null);
    }
  };

  const autoDetectarBalanza = async () => {
    const protocolo = formEdit.protocolo_balanza || 'zebra';
    setAutoDetectando(true);
    setAutoDetectProgreso('Buscando puertos...');
    setPesoTest(null);
    try {
      const resultado = await qzSerialService.autoDetectarPuerto(protocolo, setAutoDetectProgreso);
      if (resultado) {
        updateForm('puerto_balanza', resultado.puerto);
        updateForm('baudrate_balanza', resultado.baudRate);
        setPesoTest(resultado.pesoInicial);
        toast.success(`Balanza detectada en ${resultado.puerto} @ ${resultado.baudRate}`);
      } else {
        toast.warning('No se detectó ninguna balanza en los puertos disponibles');
      }
    } catch (err) {
      toast.error('Error durante la auto-detección');
      console.error(err);
    } finally {
      setAutoDetectando(false);
      setAutoDetectProgreso('');
    }
  };

  const detectarDispositivosHid = async () => {
    setBuscandoHid(true);
    setDispositivosHid([]);
    try {
      const devices = await qzHidService.listarDispositivos();
      setDispositivosHid(devices);
      if (devices.length === 0) toast.info('No se detectaron dispositivos HID');
    } catch {
      toast.error('Error al detectar dispositivos HID');
    } finally {
      setBuscandoHid(false);
    }
  };

  const updateForm = (field, value) => setFormEdit(prev => ({ ...prev, [field]: value }));

  if (cargando) return <SpinnerCargando />;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configuración POS</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure el scanner y la balanza para cada caja registradora.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {qzConectado ? (
            <span className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
              <FaCheckCircle /> QZ Tray conectado
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full">
              <FaTimesCircle /> QZ Tray no detectado
            </span>
          )}
        </div>
      </div>

      {cajas.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-500">
          No hay cajas con sesión habilitada configuradas.
        </div>
      ) : (
        <div className="space-y-6">
          {cajas.map((caja) => {
            const esEdicion = editandoId === caja.id_cuenta_tesoreria;
            const scanner = esEdicion ? formEdit.modo_scanner : (caja.modo_scanner || 'hid');
            const conexion = esEdicion ? formEdit.conexion_balanza : (caja.conexion_balanza || 'deshabilitado');

            return (
              <div
                key={caja.id_cuenta_tesoreria}
                className={`bg-white border rounded-xl shadow-sm transition-all ${
                  esEdicion ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'
                }`}
              >
                {/* Header de caja */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-800">{caja.descripcion}</h3>
                  {!esEdicion ? (
                    <button
                      onClick={() => iniciarEdicion(caja)}
                      className="px-4 py-1.5 bg-color-primario text-white rounded-lg hover:opacity-90 text-sm transition-colors"
                    >
                      Editar
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => guardarEdicion(caja.id_cuenta_tesoreria)}
                        disabled={guardando}
                        className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm transition-colors"
                      >
                        {guardando ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        onClick={cancelarEdicion}
                        disabled={guardando}
                        className="px-4 py-1.5 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-sm transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>

                <div className="p-5 space-y-5">
                  {/* Card: Scanner */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <FiCpu className="text-color-primario" size={18} />
                      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Scanner</h4>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Modo del scanner</label>
                        {esEdicion ? (
                          <select
                            value={formEdit.modo_scanner}
                            onChange={(e) => updateForm('modo_scanner', e.target.value)}
                            className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                          >
                            {MODOS_SCANNER.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-gray-800 p-2">
                            {MODOS_SCANNER.find(m => m.value === scanner)?.label || scanner}
                          </p>
                        )}
                      </div>

                      {esEdicion && formEdit.modo_scanner === 'qz_hid' && (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Vendor ID</label>
                              <input
                                type="text"
                                value={formEdit.scanner_vendor_id}
                                onChange={(e) => updateForm('scanner_vendor_id', e.target.value)}
                                placeholder="0x05E0"
                                className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 outline-none transition-all text-sm font-mono"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Product ID</label>
                              <input
                                type="text"
                                value={formEdit.scanner_product_id}
                                onChange={(e) => updateForm('scanner_product_id', e.target.value)}
                                placeholder="0x0600"
                                className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 outline-none transition-all text-sm font-mono"
                              />
                            </div>
                          </div>
                          {qzConectado && (
                            <div>
                              <button
                                type="button"
                                onClick={detectarDispositivosHid}
                                disabled={buscandoHid}
                                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm transition-colors"
                              >
                                <FiRefreshCw size={12} className={buscandoHid ? 'animate-spin' : ''} />
                                {buscandoHid ? 'Buscando...' : 'Detectar dispositivos'}
                              </button>
                              {dispositivosHid.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs text-gray-500">{dispositivosHid.length} dispositivo(s) detectado(s):</p>
                                  {dispositivosHid
                                    .slice()
                                    .sort((a, b) => (esScanner(b) ? 1 : 0) - (esScanner(a) ? 1 : 0))
                                    .map((dev, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => {
                                        if (dev.vendorId) updateForm('scanner_vendor_id', dev.vendorId);
                                        if (dev.productId) updateForm('scanner_product_id', dev.productId);
                                        toast.success('IDs del dispositivo aplicados');
                                      }}
                                      className={`block w-full text-left px-3 py-2 text-xs border rounded-lg transition-colors font-mono ${
                                        esScanner(dev)
                                          ? 'bg-green-50 border-green-300 hover:bg-green-100 hover:border-green-400'
                                          : 'bg-white border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                                      }`}
                                    >
                                      <span className="font-semibold">{dev.vendorId || '?'}:{dev.productId || '?'}</span>
                                      {' — '}
                                      {etiquetaDispositivo(dev)}
                                      {esScanner(dev) && (
                                        <span className="ml-2 text-green-700 font-semibold">(scanner)</span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {!qzConectado && (
                            <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                              QZ Tray no está conectado. Inicie QZ Tray para detectar dispositivos HID.
                            </p>
                          )}
                        </>
                      )}

                      {scanner === 'hid' && (
                        <p className="text-xs text-gray-400 bg-blue-50 rounded-lg px-3 py-2">
                          En modo HID, el scanner envía los datos como si fuera un teclado. No requiere configuración adicional.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Card: Balanza */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <FiActivity className="text-color-primario" size={18} />
                      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Balanza</h4>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Conexión</label>
                        {esEdicion ? (
                          <select
                            value={formEdit.conexion_balanza}
                            onChange={(e) => updateForm('conexion_balanza', e.target.value)}
                            className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                          >
                            {CONEXIONES_BALANZA.map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-gray-800 p-2">
                            {CONEXIONES_BALANZA.find(c => c.value === conexion)?.label || conexion}
                          </p>
                        )}
                      </div>

                      {((esEdicion && formEdit.conexion_balanza === 'serial') || (!esEdicion && conexion === 'serial')) && (
                        <>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-sm font-medium text-gray-600">Puerto serial</label>
                              {esEdicion && qzConectado && (
                                <button
                                  type="button"
                                  onClick={refrescarPuertos}
                                  disabled={refrescandoPuertos}
                                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                                >
                                  <FiRefreshCw size={11} className={refrescandoPuertos ? 'animate-spin' : ''} />
                                  Refrescar
                                </button>
                              )}
                            </div>
                            {esEdicion ? (
                              qzConectado && puertosSerial.length > 0 ? (
                                <select
                                  value={formEdit.puerto_balanza}
                                  onChange={(e) => updateForm('puerto_balanza', e.target.value)}
                                  className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                >
                                  <option value="">Seleccionar puerto...</option>
                                  {puertosSerial.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={formEdit.puerto_balanza}
                                  onChange={(e) => updateForm('puerto_balanza', e.target.value)}
                                  placeholder="COM3"
                                  className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 outline-none transition-all text-sm"
                                />
                              )
                            ) : (
                              <p className="text-gray-800 p-2">
                                {caja.puerto_balanza || <span className="text-gray-400 italic">Sin configurar</span>}
                              </p>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">Protocolo</label>
                              {esEdicion ? (
                                <select
                                  value={formEdit.protocolo_balanza}
                                  onChange={(e) => updateForm('protocolo_balanza', e.target.value)}
                                  className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                >
                                  {PROTOCOLOS.map(p => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-gray-800 p-2">
                                  {PROTOCOLOS.find(p => p.value === (caja.protocolo_balanza || 'zebra'))?.label}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">Baud Rate</label>
                              {esEdicion ? (
                                <select
                                  value={formEdit.baudrate_balanza}
                                  onChange={(e) => updateForm('baudrate_balanza', Number(e.target.value))}
                                  className="w-full p-2 rounded-xl text-gray-700 border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                >
                                  {BAUDRATES.map(b => (
                                    <option key={b.value} value={b.value}>{b.label}</option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-gray-800 p-2">{caja.baudrate_balanza || 9600}</p>
                              )}
                            </div>
                          </div>

                          {esEdicion && qzConectado && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => probarBalanza(caja.id_cuenta_tesoreria)}
                                  disabled={!formEdit.puerto_balanza || probandoId === caja.id_cuenta_tesoreria || autoDetectando}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm transition-colors"
                                >
                                  <FaSync size={12} className={probandoId === caja.id_cuenta_tesoreria ? 'animate-spin' : ''} />
                                  {probandoId === caja.id_cuenta_tesoreria ? 'Leyendo...' : 'Probar conexión'}
                                </button>
                                <button
                                  type="button"
                                  onClick={autoDetectarBalanza}
                                  disabled={autoDetectando || probandoId === caja.id_cuenta_tesoreria}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-color-primario text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm transition-colors"
                                >
                                  <FiRefreshCw size={12} className={autoDetectando ? 'animate-spin' : ''} />
                                  {autoDetectando ? 'Detectando...' : 'Auto-detectar'}
                                </button>
                                {pesoTest && (
                                  <span className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
                                    {pesoTest.peso} {pesoTest.unidad} {pesoTest.estable ? '(estable)' : '(inestable)'}
                                  </span>
                                )}
                              </div>
                              {autoDetectando && autoDetectProgreso && (
                                <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5">
                                  {autoDetectProgreso}
                                </p>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {conexion === 'deshabilitado' && (
                        <p className="text-xs text-gray-400 bg-gray-100 rounded-lg px-3 py-2">
                          La balanza está deshabilitada. El peso se obtiene del código de barras impreso por la balanza.
                        </p>
                      )}

                      {!qzConectado && esEdicion && formEdit.conexion_balanza === 'serial' && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                          <p className="text-xs text-yellow-700">
                            QZ Tray no está conectado. Inicie QZ Tray para detectar puertos seriales y probar la conexión.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ConfiguracionPOS;
